import { forwardRef, useState, useId, type InputHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { Eye, EyeOff } from "lucide-react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, type, id, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    const autoId = useId();
    const inputId = id || autoId;

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-sand-700 dark:text-sand-300">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={isPassword && showPassword ? "text" : type}
            className={cn(
              "w-full h-10 px-3 rounded-xl",
              "bg-white border border-sand-300 text-sand-900",
              "placeholder:text-sand-400 text-sm",
              "transition-colors duration-200",
              "hover:border-ocean-300",
              "focus:outline-none focus:border-ocean-500 focus:ring-2 focus:ring-ocean-500/20",
              "dark:bg-sand-800 dark:border-sand-700 dark:text-sand-100 dark:placeholder:text-sand-500",
              "dark:hover:border-ocean-400 dark:focus:border-ocean-400",
              error && "border-coral-500 focus:border-coral-500 focus:ring-coral-500/20",
              isPassword && "pr-10",
              className,
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sand-400 hover:text-sand-600 transition-colors cursor-pointer"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {error && <p className="text-xs text-coral-500 pl-0.5">{error}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";
